import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { getMostViewedContent } from "common/services/analytics.service";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";

const MostViewedContent = () => {
  const [period, setPeriod] = useState("1M");
  const [contentType, setContentType] = useState("contractor");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);

  // Convert period to API format
  const getTypeOfRequest = (period: string) => {
    switch (period) {
      case "1W":
        return "pastweek";
      case "1M":
        return "pastmonth";
      case "6M":
        return "past6months";
      case "1Y":
        return "pastyear";
      case "ALL":
        return "all";
      default:
        return "pastmonth";
    }
  };

  // Convert content type to API format
  const getContentTypeForAPI = (type: string) => {
    const contentTypeMap: { [key: string]: string } = {
      contractor: "contractor",
      "us-sanctions": "us-sanctions-list",
      "eu-sanctions": "eu-sanctions-list",
      "un-designated": "un-designated-list",
      "world-bank": "world-bank-banned-list",
      "uk-sanctions": "uk-sanctions-list",
      "ungm-list": "ungm-list",
    };

    return contentTypeMap[type] || "contractor";
  };

  useEffect(() => {
    const fetchMostViewedContent = async () => {
      setLoading(true);
      try {
        const typeOfRequest = getTypeOfRequest(period);
        const apiContentType = getContentTypeForAPI(contentType);

        const response: any = await getMostViewedContent({
          typeOfRequest,
          contentType: apiContentType,
        });

        if (response && response.data) {
          // Transform the API response to match our table format
          const transformedData = response.data.map((item: any) => {
            return {
              pageName: item.name,
              pageUrl: item.pageUrl,
              visits: item.views,
              uniqueUsers: item.uniqueUsers,
            };
          });

          setData(transformedData);
        }
      } catch (error) {
        console.error("Error fetching most viewed content:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMostViewedContent();
  }, [period, contentType]);

  const contentTypeOptions = [
    { value: "contractor", label: "Contractors" },
    { value: "us-sanctions", label: "US Sanctions List" },
    { value: "eu-sanctions", label: "EU Sanctions List" },
    { value: "un-designated", label: "UN Designated List" },
    { value: "world-bank", label: "World Bank Banned List" },
    { value: "uk-sanctions", label: "UK Sanctions List" },
    { value: "ungm-list", label: "UNGM List" },
  ];

  const selectedContentTypeLabel =
    contentTypeOptions.find((option) => option.value === contentType)?.label ||
    "Contractors";

  return (
    <React.Fragment>
      <Col xl={12}>
        <Card className="card-height-100">
          <CardHeader className="align-items-center d-flex">
            <h4 className="card-title mb-0 flex-grow-1">
              Most Viewed Content - {selectedContentTypeLabel}
            </h4>
            <div className="flex-shrink-0 d-flex gap-2">
              {/* Content Type Filter */}
              <UncontrolledDropdown className="card-header-dropdown">
                <DropdownToggle
                  tag="a"
                  className="text-reset dropdown-btn"
                  role="button"
                >
                  <span className="text-muted">
                    {selectedContentTypeLabel}
                    <i className="mdi mdi-chevron-down ms-1"></i>
                  </span>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                  {contentTypeOptions.map((option) => (
                    <DropdownItem
                      key={option.value}
                      onClick={() => setContentType(option.value)}
                      className={contentType === option.value ? "active" : ""}
                    >
                      {option.label}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>

              {/* Period Filter */}
              <div>
                <button
                  type="button"
                  className={`btn ${
                    period === "1W" ? "btn-soft-primary" : "btn-soft-secondary"
                  } btn-sm material-shadow-none me-1`}
                  onClick={() => setPeriod("1W")}
                >
                  1W
                </button>
                <button
                  type="button"
                  className={`btn ${
                    period === "1M" ? "btn-soft-primary" : "btn-soft-secondary"
                  } btn-sm material-shadow-none me-1`}
                  onClick={() => setPeriod("1M")}
                >
                  1M
                </button>
                <button
                  type="button"
                  className={`btn ${
                    period === "6M" ? "btn-soft-primary" : "btn-soft-secondary"
                  } btn-sm material-shadow-none me-1`}
                  onClick={() => setPeriod("6M")}
                >
                  6M
                </button>
                <button
                  type="button"
                  className={`btn ${
                    period === "1Y" ? "btn-soft-primary" : "btn-soft-secondary"
                  } btn-sm material-shadow-none me-1`}
                  onClick={() => setPeriod("1Y")}
                >
                  1Y
                </button>
                <button
                  type="button"
                  className={`btn ${
                    period === "ALL" ? "btn-soft-primary" : "btn-soft-secondary"
                  } btn-sm material-shadow-none`}
                  onClick={() => setPeriod("ALL")}
                >
                  ALL
                </button>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="table-responsive table-card">
              <table className="table align-middle table-borderless table-centered table-nowrap mb-0 table-striped">
                <thead className="text-muted table-light">
                  <tr>
                    <th scope="col" style={{ width: "50%" }}>
                      Content
                    </th>
                    <th scope="col">Total Visits</th>
                    <th scope="col">Unique Users</th>
                    <th scope="col">Avg. Visits per User</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <tr key={index}>
                        <td>
                          <Skeleton />
                        </td>
                        <td>
                          <Skeleton />
                        </td>
                        <td>
                          <Skeleton />
                        </td>
                        <td>
                          <Skeleton />
                        </td>
                      </tr>
                    ))
                  ) : data.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4 text-muted">
                        No data available for the selected period and content
                        type.
                      </td>
                    </tr>
                  ) : (
                    (data || []).map((item: any, index: number) => (
                      <tr key={index}>
                        <td>
                          <Link
                            to={item.pageUrl || "#"}
                            className="text-primary fw-medium"
                          >
                            {item.pageName}
                          </Link>
                        </td>
                        <td>
                          <span className="fw-semibold">{item.visits}</span>
                        </td>
                        <td>
                          <span className="fw-semibold">
                            {item.uniqueUsers}
                          </span>
                        </td>
                        <td>
                          <span className="fw-semibold">
                            {item.uniqueUsers > 0
                              ? (item.visits / item.uniqueUsers).toFixed(1)
                              : "0.0"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default MostViewedContent;
